
import * as z from "zod";

const numberRegex = /^\d+(\.\d+)?$/;

export const createReviewFormSchema = (language: string) => {
  return z.object({
    community_name: z.string().optional(),
    country: z.string({ 
      required_error: language === 'en' ? "Please select a country" : "請選擇國家" 
    }),
    region: z.string({ 
      required_error: language === 'en' ? "Please select a region" : "請選擇地區" 
    }),
    address_detail: z.string().min(5, { 
      message: language === 'en' ? "Address must be at least 5 characters" : "地址至少需要 5 個字元" 
    }).max(255, { 
      message: language === 'en' ? "Address must be at most 255 characters" : "地址最多 255 個字元" 
    }),
    rental_status: z.enum(['viewed', 'renting', 'ended'], { 
      required_error: language === 'en' ? "Rental status is required" : "請選擇租房狀態" 
    }),
    housing_type: z.enum(['basement', 'house', 'room', 'condo', 'apartment', 'semi-detached'], {
      required_error: language === 'en' ? "Housing type is required" : "請選擇房屋類型" 
    }),
    bedrooms: z.coerce.number().min(0, {
      message: language === 'en' ? "Number of bedrooms is required" : "請填寫房間數量"
    }),
    living_rooms: z.coerce.number().min(0, {
      message: language === 'en' ? "Number of living rooms is required" : "請填寫客廳數量"
    }),
    bathrooms: z.coerce.number().min(0, {
      message: language === 'en' ? "Number of bathrooms is required" : "請填寫浴室數量"
    }),
    rental_year_start: z.coerce.number().min(1900).max(new Date().getFullYear() + 10, {
      message: language === 'en' ? "Start year is required" : "請填寫起始年份"
    }),
    rental_year_end: z.union([
      z.coerce.number().min(1900).max(new Date().getFullYear() + 20),
      z.literal(""),
      z.undefined(),
      z.null()
    ]).optional().transform(val => {
      if (val === "" || val === null || val === undefined) return undefined;
      return Number(val);
    }),
    rating: z.coerce.number().min(1, { 
      message: language === 'en' ? "Rating must be between 1-5" : "評分必須介於 1-5 之間" 
    }).max(5, { 
      message: language === 'en' ? "Rating must be between 1-5" : "評分必須介於 1-5 之間" 
    }),
    comment: z.string().optional(),
    
    rent: z.string()
      .min(1, { 
        message: language === 'en' ? "Rent is required" : "請填寫租金" 
      })
      .refine((val) => {
        const trimmed = val.trim();
        return trimmed && numberRegex.test(trimmed);
      }, {
        message: language === 'en' ? "Only numbers are allowed" : "僅能輸入純數字"
      }),
    
    // Immigrant-friendly fields
    accepts_overseas_proof_of_funds: z.boolean().default(false),
    accepts_overseas_employment_proof: z.boolean().default(false),
    immigrant_friendly: z.boolean().default(false),
    
    // Pet-friendly fields
    pet_allowed: z.boolean().default(false),
    pet_fee: z.boolean().default(false),
    no_pet_limit_count: z.boolean().default(false),
    no_pet_limit_type: z.boolean().default(false),
    
    // Application ease fields
    requires_employment_proof: z.boolean().default(false),
    requires_credit_check: z.boolean().default(false),
    has_income_requirement: z.boolean().default(false),
    requires_financial_proof: z.boolean().default(false),
    no_proof_required: z.boolean().default(false),
    
    // Contract flexibility fields
    allows_daily_rental: z.boolean().default(false),
    allows_monthly_rental: z.boolean().default(false),
    allows_semiannual_rental: z.boolean().default(false),
    allows_contract_termination: z.boolean().default(false),
    allows_contract_transfer: z.boolean().default(false),
    
    deposit_type: z.enum(["amount", "months"], { 
      required_error: language === 'en' ? "Please select deposit type" : "請選擇押金類型" 
    }),
    deposit_value: z.string()
      .min(1, { 
        message: language === 'en' ? "Deposit value is required" : "請填寫押金金額" 
      })
      .refine((val) => {
        const trimmed = val.trim();
        return trimmed && numberRegex.test(trimmed);
      }, {
        message: language === 'en' ? "Only numbers are allowed" : "僅能輸入純數字"
      }),
    
    min_rental_period: z.coerce.number().optional(),
    required_documents: z.array(z.string()).default([]),
    image_urls: z.array(z.string()).default([]),
    utilities_included: z.array(z.string()).default([]),
    parking_information: z.array(z.string()).default([]),
    
    // Privacy control fields
    show_landlord_name: z.boolean().default(true),
    hide_street_number: z.boolean().default(false),
  });
};
